import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { BACKEND_ERROR_MESSAGES } from 'src/app/shared/constants/error-messages.constants';
import { WaysFindOut } from 'src/app/shared/interfaces/data-selects';
import { PrivacyTermsComponent } from 'src/app/shared/privacy-terms/privacy-terms.component';
import { RequestInformationComponent } from 'src/app/shared/request-information/request-information.component';
import { GtmService } from 'src/app/shared/services/gtm.service';
import { PostDataService } from 'src/app/shared/services/post-data.service';
import { RequestFormService } from 'src/app/shared/services/request-form.service';
import { AutoUnsubscribeComponent } from 'src/app/shared/utils/auto-unsubscribe.component';

@Component({
  selector: 'app-welcome-step-new',
  templateUrl: './welcome-step-new.component.html',
  styleUrls: ['./welcome-step-new.component.scss']
})
export class WelcomeStepNewComponent extends AutoUnsubscribeComponent {
  @Output() submitWelcome = new EventEmitter<any>();
  @Input() waysFindOut: WaysFindOut[] | undefined = undefined;
  
  public form!: FormGroup;
  public emailCtrl: FormControl = new FormControl('');
  public isFormValid = false;
  public isWaitingApi = false;
  public showTerms = false;
  public whereFindOutId = '';
  public referralId = '';
  public promoter = '';
  public showConfirmationStep = false;


  // Keep the same look & feel for country codes (re-using image path style)
  public phoneCountryCode = [
    { countryCode: 'MX', phoneCode: '+52', imageUrl: 'assets/country-flags/bandera-mexico.png' },
    { countryCode: 'CO', phoneCode: '+57', imageUrl: 'assets/country-flags/bandera-colombia.png' },
  ];

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
    private postDataService: PostDataService,
    private requestFormService: RequestFormService,
    private activatedRouter: ActivatedRoute,
    private gtmService: GtmService
  ) {
    super();

    this.activatedRouter.queryParams.subscribe((params) => {
      this.whereFindOutId = params['whereFindOutId'];
      this.referralId = params['referallId'];
      this.promoter = params['promoter'];
    });
  }

  ngOnInit(): void {
    // Regex patterns
    const EMAIL = /^[\w-.!#$%&'*+-/=?^_`{|}~]+@([\w-]+\.)+[\w-]{2,6}$/;
    const MOBILE = /^[0-9]{10}$/;

    // Build reactive form
    this.form = this.fb.group({
      firstName: ['', [Validators.required, Validators.maxLength(45), Validators.minLength(2)]],
      lastName: ['', [Validators.required, Validators.maxLength(45), Validators.minLength(2)]],
      secondLastName: ['', [Validators.maxLength(45), Validators.minLength(2)]],
      cellPhoneCountryCode: ['MX', [Validators.required]],
      mobilePhoneNumber: ['', [Validators.required, Validators.pattern(MOBILE)]],
      privacyNoticeAccepted: [false, [Validators.requiredTrue]],
    });

    // Email as standalone control (to mirror LTO layout)
    this.emailCtrl.setValidators([Validators.required, Validators.pattern(EMAIL)]);

    // Track overall validity
    this.form.statusChanges.subscribe(() => this.checkValidity());
    this.emailCtrl.statusChanges?.subscribe(() => this.checkValidity());
  }

    openNoticeOfPrivacyDialog() {
      const dialogConfig = new MatDialogConfig();
      dialogConfig.maxHeight = '90vh';
      const dialogSubscription$ = this.dialog
        .open(PrivacyTermsComponent, dialogConfig)
        .afterClosed()
        .subscribe(() => []);
      this.subscriptions.push(dialogSubscription$);
    }

  // Utility that keeps the UX fast and consistent
  checkValidity(): void {
    this.isFormValid = this.form.valid && this.emailCtrl.valid;
  }

  getCountry() {
    return !this.requestFormService.getUpdateForm()
      ? sessionStorage.getItem('language')!
      : this.requestFormService.getUpdateForm().countryCode.code.toLowerCase();
  }

  openRequestInformationDialog(err: HttpErrorResponse) {
    const dropModal = this.dialog
      .open(RequestInformationComponent, {
        maxWidth: '90vh',
        data: {
          title: 'Información de solicitud',
          question: this.resolveErrorMessage(err)
        }
      });

    const closedSubscription$ = dropModal.afterClosed().subscribe(() => []);
    this.subscriptions.push(closedSubscription$);
  }

  onSubmit(): void {
    if (!this.isFormValid || this.isWaitingApi) return;
    
    this.isWaitingApi = true;

    let assignedToMaria = true;
    if (this.waysFindOut?.find(way => way.id === this.referralId)?.assignToAIAgent === false) { 
      assignedToMaria = false;
    }

    console.log(this.waysFindOut)
    const payload = {
      ...this.form.value,
      email: this.emailCtrl.value,
      whereFindOutId: this.whereFindOutId ?? this.referralId ?? null,
      assignedToMaria,
      ...(this.promoter ? { promoterId: this.promoter } : {})
    };

    this.postDataService.postSaveDataWelcome(this.getCountry().toUpperCase(), payload).subscribe({
      next: () => {
        this.isWaitingApi = false;
        this.submitWelcome.emit(payload);
        this.form.reset();
        this.emailCtrl.reset();
        this.form.get('cellPhoneCountryCode')?.setValue('MX');
        this.showConfirmationStep = true;

        // Push GTM event for login.
        this.gtmService.pushLoginEvent();
      },
      error: (err) => { 
        this.openRequestInformationDialog(err);
        this.isWaitingApi = false;
      }
    });
  }

  // English-only comments: helpers
  isLetter(evt: KeyboardEvent) {
    if (
      evt.code !== 'Space' &&
      evt.code !== 'Backspace' &&
      evt.code !== 'Delete' &&
      evt.code !== 'ArrowLeft' &&
      evt.code !== 'ArrowRight' &&
      evt.code !== 'Tab' &&
      (evt.keyCode < 65 || (evt.keyCode > 90 && evt.keyCode !== 192))
    ) {
      evt.preventDefault();
    }
  }

  deleteSpaceInit(evt: KeyboardEvent) {
    (evt.target as HTMLInputElement)?.addEventListener('keyup', (e: any) => {
      const string = e.target!.value;
      e.target.value = string.trimStart();
    });
  }

  private resolveErrorMessage(err: HttpErrorResponse): string {
    const errorCode = err.error?.errors?.[0]?.code;
    return BACKEND_ERROR_MESSAGES[errorCode] ?? err.error?.errors?.[0]?.message;
  }
}
